import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Autocomplete,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Divider,
  SelectChangeEvent,
  useTheme,
  useMediaQuery,
  FormHelperText,
  Alert,
  IconButton,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  Save as SaveIcon,
  Cake as CakeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { ProfileUpdateData, GRADUATION_STATUS_OPTIONS, GraduationStatus } from '../../../types/profile';
import {
  PREFECTURES,
  REAL_ESTATE_JOB_TYPES,
  COMPANY_TYPES,
  COMMON_REAL_ESTATE_QUALIFICATIONS,
  getPrefectureName,
  getJobTypeName,
  getCompanyTypeName,
} from '../../../constants/prefectures';
import { disabledTextFieldStyles } from './utils';

// フィールドエラーの型定義
interface FieldErrors {
  birthDate?: string;
  university?: string;
  faculty?: string;
  graduationYear?: string;
  graduationStatus?: string;
}

interface CareerProfileFormProps {
  formData: ProfileUpdateData;
  onInputChange: (field: keyof ProfileUpdateData) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onQualificationsChange: (qualifications: string[]) => void;
  isEditing: boolean;
  isUpdating: boolean;
  error?: string | null;
  onSave: () => Promise<void>;
  onCancel: () => void;
  onEdit?: () => void;
  onClearError?: () => void;
}

export const CareerProfileForm: React.FC<CareerProfileFormProps> = ({
  formData,
  onInputChange,
  onQualificationsChange,
  isEditing,
  isUpdating,
  error,
  onSave,
  onCancel,
  onEdit,
  onClearError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [newQualification, setNewQualification] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // バリデーション関数
  const validateField = (field: keyof FieldErrors, value: string): string | undefined => {
    switch (field) {
      case 'graduationYear':
        if (value && value.trim()) {
          const year = parseInt(value);
          if (isNaN(year)) return '卒業年は数値で入力してください';
          const currentYear = new Date().getFullYear();
          if (year < 1900 || year > currentYear + 10) {
            return `卒業年は1900年以降、${currentYear + 10}年以前で入力してください`;
          }
        }
        break;
      case 'university':
        if (value && value.length > 100) return '大学名は100文字以内で入力してください';
        break;
      case 'faculty':
        if (value && value.length > 100) return '学部・学科名は100文字以内で入力してください';
        break;
      case 'birthDate':
        if (value && value.trim()) {
          const date = new Date(value);
          if (isNaN(date.getTime())) return '正しい日付形式で入力してください';
        }
        break;
    }
    return undefined;
  };

  // フィールド変更時のバリデーション
  const validateAndUpdateField = (field: keyof FieldErrors, value: string) => {
    const fieldError = validateField(field, value);
    setFieldErrors(prev => ({
      ...prev,
      [field]: fieldError,
    }));
  };

  // バリデーション付きの入力変更ハンドラー
  const handleInputChangeWithValidation = (field: keyof ProfileUpdateData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    
    // リアルタイムバリデーション（学歴情報フィールドのみ）
    if (['university', 'faculty', 'graduationYear'].includes(field as string)) {
      validateAndUpdateField(field as keyof FieldErrors, value);
    }
    
    // 元の処理を呼び出し
    onInputChange(field)(event);
  };

  // 保存処理（バリデーション付き）
  const handleSave = async () => {
    // 学歴情報のバリデーション
    const errors: FieldErrors = {};
    
    const graduationYearError = validateField('graduationYear', formData.graduationYear?.toString() || '');
    if (graduationYearError) errors.graduationYear = graduationYearError;
    
    const universityError = validateField('university', formData.university || '');
    if (universityError) errors.university = universityError;
    
    const facultyError = validateField('faculty', formData.faculty || '');
    if (facultyError) errors.faculty = facultyError;
    
    const birthDateError = validateField('birthDate', formData.birthDate || '');
    if (birthDateError) errors.birthDate = birthDateError;
    
    setFieldErrors(errors);
    
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) {
      // エラーがある場合は保存を阻止
      return;
    }
    
    // バリデーション成功時のみ保存実行
    await onSave();
  };

  // 生年月日の解析と状態管理
  const parseBirthDate = (value: string | undefined) => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return {
          year:  date.getFullYear().toString(),
          month: (date.getMonth() + 1).toString().padStart(2, '0'),
          day:   date.getDate().toString().padStart(2, '0'),
        };
      }
    }
    return { year: '', month: '', day: '' };
  };

  const [birthDateParts, setBirthDateParts] = useState(() => parseBirthDate(formData.birthDate));

  // formData が非同期で読み込まれた後に birthDateParts を同期する
  useEffect(() => {
    if (formData.birthDate) {
      setBirthDateParts(parseBirthDate(formData.birthDate));
    }
  }, [formData.birthDate]);

  // 年、月、日の選択肢を生成
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 1950; year--) {
      years.push(year.toString());
    }
    return years;
  }, []);

  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  }, []);

  const dayOptions = useMemo(() => {
    if (!birthDateParts.year || !birthDateParts.month) {
      return Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, '0'));
    }

    const year = parseInt(birthDateParts.year);
    const month = parseInt(birthDateParts.month);
    const daysInMonth = new Date(year, month, 0).getDate();
    
    return Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString().padStart(2, '0'));
  }, [birthDateParts.year, birthDateParts.month]);

  // 生年月日の変更ハンドラー
  const handleBirthDateChange = (field: 'year' | 'month' | 'day') => (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    const newParts = { ...birthDateParts, [field]: value };
    setBirthDateParts(newParts);

    // 年、月、日がすべて入力されている場合は、ISO形式に変換
    if (newParts.year && newParts.month && newParts.day) {
      const isoDate = `${newParts.year}-${newParts.month}-${newParts.day}`;
      const customEvent = {
        target: { value: isoDate }
      } as React.ChangeEvent<HTMLInputElement>;
      onInputChange('birthDate')(customEvent);
      
      // 完全な日付が入力されたのでバリデーションを実行
      validateAndUpdateField('birthDate', isoDate);
    } else {
      // 不完全な場合は空文字にする
      const customEvent = {
        target: { value: '' }
      } as React.ChangeEvent<HTMLInputElement>;
      onInputChange('birthDate')(customEvent);
      
      // 不完全な場合はエラーをクリア
      setFieldErrors(prev => ({
        ...prev,
        birthDate: undefined,
      }));
    }
  };

  // 年齢の自動計算
  const calculatedAge = useMemo(() => {
    if (!formData.birthDate) return null;
    
    const today = new Date();
    const birthDate = new Date(formData.birthDate);
    
    if (isNaN(birthDate.getTime())) return null;
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age >= 0 ? age : null;
  }, [formData.birthDate]);

  // 年齢表示テキスト
  const ageDisplayText = useMemo(() => {
    if (calculatedAge === null) return '';
    return `（${calculatedAge}歳）`;
  }, [calculatedAge]);

  // 資格追加のハンドラー
  const handleAddQualification = (value: string) => {
    if (!value.trim()) return;
    
    const currentQualifications = formData.qualifications || [];
    if (currentQualifications.includes(value.trim())) return;
    
    const updatedQualifications = [...currentQualifications, value.trim()];
    
    // 専用のハンドラーを使用して配列を直接更新
    onQualificationsChange(updatedQualifications);
    setNewQualification('');
  };

  // 資格削除のハンドラー
  const handleRemoveQualification = (qualificationToRemove: string) => {
    const currentQualifications = formData.qualifications || [];
    const updatedQualifications = currentQualifications.filter(q => q !== qualificationToRemove);
    
    // 専用のハンドラーを使用して配列を直接更新
    onQualificationsChange(updatedQualifications);
  };

  // 都道府県変更のハンドラー
  const handlePrefectureChange = (field: 'currentPrefecture' | 'desiredPrefecture') => 
    (event: SelectChangeEvent<string>) => {
      const customEvent = {
        target: { value: event.target.value as string }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onInputChange(field)(customEvent);
    };

  // 選択値変更のハンドラー
  const handleSelectChange = (field: keyof ProfileUpdateData) => 
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value as string;
      const customEvent = {
        target: { value }
      } as React.ChangeEvent<HTMLInputElement>;
      
      // 卒業状況の場合はバリデーションを実行
      if (field === 'graduationStatus') {
        validateAndUpdateField('graduationStatus', value);
      }
      
      onInputChange(field)(customEvent);
    };

  return (
    <Card elevation={1}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="space-between" 
          mb={3}
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 2, sm: 0 }}
        >
          <Box display="flex" alignItems="center">
            <WorkIcon color="primary" fontSize="large" sx={{ mr: 1 }} />
            <Typography 
              variant="h5" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}
            >
              キャリア・希望情報
            </Typography>
          </Box>
          
          {!isEditing && (
            <Button
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              onClick={onEdit}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              編集
            </Button>
          )}
        </Box>

        {/* エラーメッセージ表示 */}
        {error && isEditing && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              onClearError && (
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={onClearError}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              )
            }
          >
            {error}
          </Alert>
        )}

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* 学歴情報セクション */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" mb={2}>
              <CakeIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
              <Typography 
                variant="subtitle1" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
              >
                学歴情報
              </Typography>
            </Box>
          </Grid>

          {/* 生年月日 - プルダウン形式 */}
          <Grid item xs={12}>
            <Box>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                mb={1}
                sx={{ fontSize: { xs: '0.875rem', sm: '0.9rem' } }}
              >
                生年月日 {ageDisplayText}
              </Typography>
              <Box 
                display="flex" 
                gap={1} 
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <FormControl 
                  size={isMobile ? 'small' : 'medium'} 
                  sx={{ minWidth: { xs: '100%', sm: 120 } }}
                  disabled={!isEditing || isUpdating}
                >
                  <InputLabel>年</InputLabel>
                  <Select
                    value={birthDateParts.year}
                    onChange={handleBirthDateChange('year')}
                    label="年"
                  >
                    <MenuItem value="">
                      <em>選択してください</em>
                    </MenuItem>
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}年
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl 
                  size={isMobile ? 'small' : 'medium'} 
                  sx={{ minWidth: { xs: '100%', sm: 80 } }}
                  disabled={!isEditing || isUpdating}
                >
                  <InputLabel>月</InputLabel>
                  <Select
                    value={birthDateParts.month}
                    onChange={handleBirthDateChange('month')}
                    label="月"
                  >
                    <MenuItem value="">
                      <em>選択</em>
                    </MenuItem>
                    {monthOptions.map((month) => (
                      <MenuItem key={month} value={month}>
                        {parseInt(month)}月
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl 
                  size={isMobile ? 'small' : 'medium'} 
                  sx={{ minWidth: { xs: '100%', sm: 80 } }}
                  disabled={!isEditing || isUpdating}
                >
                  <InputLabel>日</InputLabel>
                  <Select
                    value={birthDateParts.day}
                    onChange={handleBirthDateChange('day')}
                    label="日"
                  >
                    <MenuItem value="">
                      <em>選択</em>
                    </MenuItem>
                    {dayOptions.map((day) => (
                      <MenuItem key={day} value={day}>
                        {parseInt(day)}日
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {fieldErrors.birthDate && (
                <Typography 
                  variant="caption" 
                  color="error" 
                  sx={{ 
                    mt: 1, 
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    display: 'block'
                  }}
                >
                  {fieldErrors.birthDate}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="大学名"
              value={formData.university || ''}
              onChange={handleInputChangeWithValidation('university')}
              onBlur={() => validateAndUpdateField('university', formData.university || '')}
              disabled={!isEditing || isUpdating}
              sx={disabledTextFieldStyles}
              size={isMobile ? 'small' : 'medium'}
              placeholder="例：東京大学"
              error={!!fieldErrors.university}
              helperText={fieldErrors.university}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="学部・学科名"
              value={formData.faculty || ''}
              onChange={handleInputChangeWithValidation('faculty')}
              onBlur={() => validateAndUpdateField('faculty', formData.faculty || '')}
              disabled={!isEditing || isUpdating}
              sx={disabledTextFieldStyles}
              size={isMobile ? 'small' : 'medium'}
              placeholder="例：工学部情報工学科"
              error={!!fieldErrors.faculty}
              helperText={fieldErrors.faculty}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="卒業年"
              type="number"
              value={formData.graduationYear || ''}
              onChange={handleInputChangeWithValidation('graduationYear')}
              onBlur={() => validateAndUpdateField('graduationYear', formData.graduationYear?.toString() || '')}
              disabled={!isEditing || isUpdating}
              sx={disabledTextFieldStyles}
              size={isMobile ? 'small' : 'medium'}
              inputProps={{
                min: 1900,
                max: new Date().getFullYear() + 10,
              }}
              placeholder="例：2024"
              error={!!fieldErrors.graduationYear}
              helperText={fieldErrors.graduationYear}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              disabled={!isEditing || isUpdating} 
              sx={disabledTextFieldStyles}
              size={isMobile ? 'small' : 'medium'}
              error={!!fieldErrors.graduationStatus}
            >
              <InputLabel sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                卒業状況
              </InputLabel>
              <Select
                value={formData.graduationStatus || ''}
                label="卒業状況"
                onChange={handleSelectChange('graduationStatus')}
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  },
                }}
              >
                <MenuItem value="">選択してください</MenuItem>
                {GRADUATION_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.graduationStatus && (
                <FormHelperText>
                  {fieldErrors.graduationStatus}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* エリア情報セクション */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" alignItems="center" mb={2}>
              <LocationIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
              <Typography 
                variant="subtitle1" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
              >
                エリア情報
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              disabled={!isEditing || isUpdating} 
              sx={disabledTextFieldStyles}
              size={isMobile ? 'small' : 'medium'}
            >
              <InputLabel sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                現在のエリア
              </InputLabel>
              <Select
                value={formData.currentPrefecture || ''}
                label="現在のエリア"
                onChange={handlePrefectureChange('currentPrefecture')}
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  },
                }}
              >
                <MenuItem value="">選択してください</MenuItem>
                {PREFECTURES.map((prefecture) => (
                  <MenuItem key={prefecture.code} value={prefecture.code}>
                    {prefecture.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              disabled={!isEditing || isUpdating} 
              sx={disabledTextFieldStyles}
              size={isMobile ? 'small' : 'medium'}
            >
              <InputLabel sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                働く希望エリア
              </InputLabel>
              <Select
                value={formData.desiredPrefecture || ''}
                label="働く希望エリア"
                onChange={handlePrefectureChange('desiredPrefecture')}
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  },
                }}
              >
                <MenuItem value="">選択してください</MenuItem>
                {PREFECTURES.map((prefecture) => (
                  <MenuItem key={prefecture.code} value={prefecture.code}>
                    {prefecture.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* 希望業種セクション */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" alignItems="center" mb={2}>
              <BusinessIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
              <Typography 
                variant="subtitle1" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
              >
                希望業種
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <FormControl 
              fullWidth 
              disabled={!isEditing || isUpdating} 
              sx={disabledTextFieldStyles}
              size={isMobile ? 'small' : 'medium'}
            >
              <InputLabel sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                希望の業種
              </InputLabel>
              <Select
                value={formData.desiredJobType || ''}
                label="希望の業種"
                onChange={handleSelectChange('desiredJobType')}
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  },
                }}
              >
                <MenuItem value="">選択してください</MenuItem>
                {REAL_ESTATE_JOB_TYPES.map((jobType) => (
                  <MenuItem key={jobType.value} value={jobType.value}>
                    {jobType.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* 資格セクション */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" alignItems="center" mb={2}>
              <SchoolIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
              <Typography 
                variant="subtitle1" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
              >
                資格
              </Typography>
            </Box>
          </Grid>

          {isEditing && (
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={COMMON_REAL_ESTATE_QUALIFICATIONS}
                value={null}
                inputValue={newQualification}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string' && newValue.trim()) {
                    handleAddQualification(newValue);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  setNewQualification(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="資格を追加"
                    placeholder="資格名を入力または選択"
                    disabled={isUpdating}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddQualification(newQualification);
                      }
                    }}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                      },
                    }}
                  />
                )}
                disabled={isUpdating}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: { xs: 0.5, sm: 1 }, 
              minHeight: '40px' 
            }}>
              {(Array.isArray(formData.qualifications) ? formData.qualifications : []).map((qualification, index) => (
                <Chip
                  key={index}
                  label={qualification}
                  onDelete={isEditing && !isUpdating ? () => handleRemoveQualification(qualification) : undefined}
                  color="primary"
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    height: { xs: 24, sm: 32 },
                  }}
                />
              ))}
            </Box>
            <Box sx={{ mt: 1 }}>
              {(!Array.isArray(formData.qualifications) || formData.qualifications.length === 0) ? (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    fontStyle: 'italic',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  }}
                >
                  {isEditing ? '資格が登録されていません' : '編集ボタンを押して資格を追加してください'}
                </Typography>
              ) : (
                !isEditing && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      fontStyle: 'italic',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    }}
                  >
                    編集ボタンを押して資格を追加・編集できます
                  </Typography>
                )
              )}
            </Box>
          </Grid>
        </Grid>

        {isEditing && (
          <Box 
            display="flex" 
            gap={2} 
            mt={3}
            flexDirection={{ xs: 'column', sm: 'row' }}
          >
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isUpdating}
              startIcon={isUpdating ? <CircularProgress size={20} /> : <SaveIcon />}
              size={isMobile ? 'small' : 'medium'}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {isUpdating ? '保存中...' : '保存'}
            </Button>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={isUpdating}
              size={isMobile ? 'small' : 'medium'}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              キャンセル
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}; 